import React, { useState, useEffect, useRef } from "react";
import { Button, Card, Alert, Spinner } from "react-bootstrap";
import { FaCreditCard, FaLock, FaExclamationTriangle } from "react-icons/fa";
import CustomModal from "../../Global/Modal/CustomModal";
import "./InAppPaymentModal.css";
import {
    InitTransaction,
    TRANSACTION_TYPES,
    getRegistrationStatus,
} from "../../../backend/paymentsFunctions";

const InAppPaymentModal = ({
    isOpen,
    userData,
    onPaymentComplete,
    onClose,
    isSuspended = false,
    suspensionReason = '',
    currentScenario = null,
    trialDaysRemaining = 0,
    billingFailureDaysRemaining = 0,
}) => {
    const [paymentIframe, setPaymentIframe] = useState(null);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [iframeLoading, setIframeLoading] = useState(true);
    const [registrationDocId, setRegistrationDocId] = useState(null);
    const [isCheckingPayment, setIsCheckingPayment] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState("initializing");

    // Add refs to track initialization state
    const hasInitialized = useRef(false);
    const initializationInProgress = useRef(false);

    // Check if user is a coach
    const isCoach = userData?.userType === "coach";

    // Initialize payment iframe when component mounts
    useEffect(() => {

        // Reset initialization state when modal opens/closes
        if (!isOpen) {
            hasInitialized.current = false;
            initializationInProgress.current = false;
            setPaymentStatus("initializing");
            setPaymentIframe(null);
            setRegistrationDocId(null);
            setPaymentError(null);
            setIframeLoading(true);
            return;
        }

        // Don't initialize if already done or in progress
        if (hasInitialized.current || initializationInProgress.current) {
            return;
        }

        if (userData) {
            // For coaches, don't initialize payment - just show the message
            if (isCoach) {
                setPaymentStatus("coach_waiting");
                hasInitialized.current = true;
                return;
            }

            // For suspended accounts with expired grace period, show suspended message
            if (isSuspended && suspensionReason === 'grace_period_expired') {
                setPaymentStatus("suspended_account");
                hasInitialized.current = true;
                return;
            }

            // Initialize payment only once
            initializationInProgress.current = true;
            initializePayment();
        }
    }, [isOpen, userData, isCoach, isSuspended, suspensionReason]);

    // Add timeout for iframe loading
    useEffect(() => {
        if (paymentIframe && iframeLoading) {
            const timeout = setTimeout(() => {
                console.warn("Iframe loading timeout - iframe may be stuck");
                setIframeLoading(false);
            }, 10000); // 10 second timeout

            return () => clearTimeout(timeout);
        }
    }, [paymentIframe, iframeLoading]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            hasInitialized.current = false;
            initializationInProgress.current = false;
        };
    }, []);

    const initializePayment = async () => {
        try {
            setIsLoadingPayment(true);
            setPaymentError(null);
            setPaymentStatus("initializing");

            // Initialize payment transaction (J2 for registration and token creation)
            const result = await InitTransaction({
                transactionType: TRANSACTION_TYPES.REGISTRATION,
            });

            // Handle J2 response format
            if (result.data) {
                // Check for errors in the response
                if (result.data.Error && result.data.Error.ErrCode) {
                    console.error("Payment gateway error:", result.data.Error);
                    throw new Error(
                        `Payment gateway error: ${result.data.Error.ErrMsg} (Code: ${result.data.Error.ErrCode})`
                    );
                }

                // Store the registration document ID for later use
                if (result.registrationDocId) {
                    setRegistrationDocId(result.registrationDocId);
                }

                // Set the payment iframe URL - check multiple possible field names for J2 registration
                let iframeUrl = null;

                if (result.data.URL) {
                    iframeUrl = result.data.URL;
                } else if (result.data.iframeURL) {
                    iframeUrl = result.data.iframeURL;
                } else if (result.data.url) {
                    iframeUrl = result.data.url;
                } else if (result.data.paymentUrl) {
                    iframeUrl = result.data.paymentUrl;
                } else if (result.data.registrationUrl) {
                    iframeUrl = result.data.registrationUrl;
                } else if (result.data.iframe_url) {
                    iframeUrl = result.data.iframe_url;
                }

                if (iframeUrl) {
                    setPaymentIframe(iframeUrl);
                    setPaymentStatus("iframe_ready");
                    hasInitialized.current = true;
                } else {
                    console.error("Response data structure:", result.data);
                    console.error("No valid URL field found in response");
                    console.error("Full response:", result);

                    // Provide more detailed error information for debugging
                    const availableFields = Object.keys(result.data || {});
                    const errorDetails =
                        availableFields.length > 0
                            ? `Available fields: ${availableFields.join(", ")}`
                            : "No data fields available";

                    throw new Error(
                        `Payment iframe URL not received from payment gateway. ${errorDetails}. Transaction type: ${result.transactionType || "unknown"
                        }`
                    );
                }
            } else {
                throw new Error("No data received from payment gateway");
            }
        } catch (error) {
            console.error("Payment initialization failed:", error);
            setPaymentError(error.message);
            setPaymentStatus("error");
            hasInitialized.current = true; // Mark as initialized even on error to prevent retries
        } finally {
            setIsLoadingPayment(false);
            initializationInProgress.current = false;
        }
    };

    // Handle iframe load events
    const handleIframeLoad = (e) => {
        // Hide loading indicator
        setIframeLoading(false);
        setPaymentStatus("iframe_loaded");

        // Try to access iframe content (for debugging)
        try {
            const iframe = e.target;
        } catch (error) {

        }
    };

    const handleIframeError = (e) => {
        console.error("Iframe error:", e);
        setIframeLoading(false);
        setPaymentStatus("error");
    };

    const handlePaymentComplete = async () => {
        try {
            setIsCheckingPayment(true);
            setPaymentError(null);
            setPaymentStatus("checking");

            if (!registrationDocId) {
                throw new Error(
                    "מזהה מסמך הרישום לא זמין. אנא נסה שוב את תהליך התשלום."
                );
            }

            // Read the registration document to check payment status
            const registrationResult = await getRegistrationStatus(registrationDocId);
            const registrationData = registrationResult.data;

            // Check if payment was successful
            if (!registrationData.status || registrationData.status !== "success") {
                throw new Error(
                    `התשלום עדיין לא הושלם או נכשל. סטטוס נוכחי: ${registrationData.status || "לא ידוע"}. אנא המתן או נסה שוב.`
                );
            }

            if (!registrationData.token) {
                throw new Error(
                    "לא התקבל אסימון תשלום. אנא נסה שוב את תהליך התשלום."
                );
            }

            // Create payment details object as specified
            const paymentDetails = {
                plan: userData.paymentDetails?.plan || "bronze",
                didPay: userData.paymentDetails?.didPay || false,
                trialPeriodInMonths: userData.paymentDetails?.trialPeriodInMonths || 3,
                lastPaymentDate: userData.paymentDetails?.lastPaymentDate || null, // Keep existing, don't update for registration
                registrationDate: new Date(), // This is the registration date
                userSuspended: false, // Reactivate the account
                token: registrationData.token,
                lastBillingAttemptDate: null,
                lastBillingStatus: null,
                registrationDocId,
            };

            setPaymentStatus("success");

            // Call the callback to update user data
            onPaymentComplete(paymentDetails);
        } catch (error) {
            console.error("Error in payment completion:", error);
            setPaymentError(error.message);
            setPaymentStatus("error");
        } finally {
            setIsCheckingPayment(false);
        }
    };

    const handleRetry = () => {
        setPaymentError(null);
        setPaymentStatus("initializing");
        hasInitialized.current = false;
        initializationInProgress.current = false;
        setPaymentIframe(null);
        setRegistrationDocId(null);
        setIframeLoading(true);
        initializePayment();
    };

    // Calculate trial end date for display
    const getTrialEndDate = () => {
        if (!userData?.userCreatedAt || !userData?.paymentDetails?.trialPeriodInMonths) {
            return null;
        }

        let creationDate;
        try {
            if (userData.userCreatedAt.toDate) {
                creationDate = userData.userCreatedAt.toDate();
            } else if (userData.userCreatedAt.seconds) {
                creationDate = new Date(userData.userCreatedAt.seconds * 1000);
            } else if (typeof userData.userCreatedAt === 'string') {
                creationDate = new Date(userData.userCreatedAt);
            } else {
                creationDate = userData.userCreatedAt;
            }

            // Validate the date
            if (!creationDate || isNaN(creationDate.getTime())) {
                console.warn('Invalid userCreatedAt in InAppPaymentModal:', userData.userCreatedAt);
                return null;
            }

            const trialEndDate = new Date(creationDate);
            trialEndDate.setDate(trialEndDate.getDate() + ((userData.paymentDetails.trialPeriodInMonths || 3) * 30));

            return trialEndDate;
        } catch (error) {
            console.error('Error calculating trial end date:', error);
            return null;
        }
    };

    // Check if user is missing required payment fields
    const getMissingFieldsMessage = () => {
        if (!userData?.paymentDetails) {
            return "חסרים פרטי תשלום. אנא השלם את רישום כרטיס האשראי.";
        }

        const requiredFields = ['didPay', 'trialPeriodInMonths', 'token'];
        const missingFields = requiredFields.filter(field => !(field in userData.paymentDetails));

        if (missingFields.length > 0) {
            return `חסרים שדות נדרשים: ${missingFields.join(', ')}. אנא השלם את רישום כרטיס האשראי.`;
        }

        return null;
    };

    const missingFieldsMessage = getMissingFieldsMessage();
    const planName = userData?.paymentDetails?.plan || "bronze";
    const planPrices = {
        bronze: 125,
        silver: 165,
        gold: 325,
    };

    // Determine modal title and subtitle based on user status
    const getModalTitle = () => {
        if (isSuspended && suspensionReason === 'grace_period_expired') {
            return "החשבון שלך מושהה";
        }
        if (isCoach) {
            return "תקופת הניסיון הסתיימה";
        }

        // Handle new scenarios
        if (currentScenario === 'A2') {
            return "תזכורת תשלום";
        }
        if (currentScenario === 'D1') {
            return "לא הצלחנו לחייב אותך";
        }
        if (currentScenario === 'D2') {
            return "לא הצלחנו לחייב אותך";
        }
        if (currentScenario === 'B') {
            return "תקופת הניסיון הסתיימה";
        }
        if (currentScenario === 'F') {
            return "החשבון שלך מושהה";
        }
        if (currentScenario === 'G') {
            return "השלמת פרטי תשלום";
        }
        if (currentScenario === 'H') {
            return "תוכנית תשלום מיוחדת";
        }

        // Fallback for other scenarios
        if (currentScenario === 'D') {
            return "לא הצלחנו לחייב אותך";
        }
        if (missingFieldsMessage) {
            return "השלמת פרטי תשלום";
        }
        return "תקופת הניסיון הסתיימה";
    };

    const getModalSubtitle = () => {
        if (isSuspended && suspensionReason === 'grace_period_expired') {
            return "חשבון מושהה. כדי להפעיל מחדש את החשבון, הזן את פרטי אמצעי התשלום והפעל מחדש את החשבון.";
        }
        if (isCoach) {
            return "מנהל הארגון צריך לסדיר את אמצעי התשלום כדי שתוכל להמשיך להשתמש במערכת.";
        }

        // Handle new scenarios
        if (currentScenario === 'A2') {
            return `בעוד ${trialDaysRemaining} ימים תקופת הניסיון תיגמר. מומלץ להסדיר אמצעי תשלום מראש.`;
        }
        if (currentScenario === 'D1') {
            return `בבקשה הזן פרטי אמצעי תשלום אחר. יש לך תקופת חסד של ${7 - billingFailureDaysRemaining} ימים.`;
        }
        if (currentScenario === 'D2') {
            return "בבקשה הזן פרטי אמצעי תשלום אחר.";
        }
        if (currentScenario === 'B') {
            return "כדי להמשיך להשתמש ב-SimplyCoach, השלם את פרטי התשלום להפעלת המנוי שלך";
        }
        if (currentScenario === 'F') {
            return "כדי להפעיל מחדש את החשבון, הזן את פרטי אמצעי התשלום והפעל מחדש את החשבון.";
        }
        if (currentScenario === 'G') {
            return "חסרים פרטי תשלום נדרשים. אנא השלם את רישום כרטיס האשראי כדי להמשיך להשתמש במערכת.";
        }
        if (currentScenario === 'H') {
            return "יש לך תוכנית תשלום מיוחדת - אין צורך בתשלום.";
        }

        // Fallback for other scenarios
        if (currentScenario === 'D') {
            return "בבקשה הזן פרטי אמצעי תשלום אחר.";
        }
        if (missingFieldsMessage) {
            return "חסרים פרטי תשלום נדרשים. אנא השלם את רישום כרטיס האשראי כדי להמשיך להשתמש במערכת.";
        }
        return "כדי להמשיך להשתמש ב-SimplyCoach, השלם את פרטי התשלום להפעלת המנוי שלך";
    };

    // Footer buttons based on payment status
    const getFooterButtons = () => {
        if (paymentStatus === "success") {
            return [
                {
                    title: "סגור",
                    style: "primary",
                    onClick: onClose,
                },
            ];
        }

        if (paymentStatus === "error") {
            return [
                {
                    title: "נסה שוב",
                    style: "warning",
                    onClick: handleRetry,
                },
            ];
        }

        if (paymentStatus === "coach_waiting") {
            return [];
        }

        if (paymentStatus === "suspended_account") {
            return [];
        }

        if (paymentStatus === "iframe_loaded" && registrationDocId) {
            return [
                {
                    title: "סיום",
                    style: "success",
                    onClick: handlePaymentComplete,
                    disabled: isCheckingPayment,
                },
            ];
        }

        // Add close button for dismissible scenarios
        if (currentScenario === 'A2' || currentScenario === 'D1') {
            return [
                {
                    title: "סגור",
                    style: "secondary",
                    onClick: onClose,
                },
            ];
        }

        return [];
    };

    // Safety check - don't render if userData is not properly loaded
    if (!userData || !userData.paymentDetails) {
        console.warn('InAppPaymentModal: userData or paymentDetails not available');
        return null;
    }

    return (
        <CustomModal
            isOpen={isOpen}
            onClose={currentScenario === 'A2' || currentScenario === 'D1' ? onClose : () => { }} // Allow closing for dismissible scenarios
            title={paymentStatus === "error" ? "" : getModalTitle()}
            width={40}
            height={90}
            footerButtons={getFooterButtons()}
            formErrorMessage={null}
        >
            {/* Suspended Account State */}
            {paymentStatus === "suspended_account" ? (
                <div className="suspended-account-container">
                    <div className="suspended-account-icon-wrapper">
                        <div className="suspended-account-icon">
                            <span className="suspended-account-icon-text">🚫</span>
                        </div>
                    </div>
                    <div className="suspended-account-message">
                        <h3 className="suspended-account-title">החשבון שלך מושהה</h3>
                        <p className="suspended-account-subtitle">
                            בשביל להשתמש במערכת, הפעל מחדש את חשבונך.
                        </p>
                        <div className="suspended-account-actions">
                            <Button
                                variant="danger"
                                size="lg"
                                onClick={() => {
                                    hasInitialized.current = false;
                                    initializationInProgress.current = false;
                                    setPaymentIframe(null);
                                    setRegistrationDocId(null);
                                    setPaymentError(null);
                                    setIframeLoading(true);
                                    setPaymentStatus("initializing");
                                    initializePayment();
                                }}
                                disabled={isLoadingPayment}
                                className="reactivate-button"
                            >
                                {isLoadingPayment ? "טוען..." : "הפעל מחדש"}
                            </Button>
                        </div>
                    </div>
                </div>
            ) : paymentStatus === "coach_waiting" ? (
                <div className="coach-waiting-container">
                    <div className="coach-waiting-icon-wrapper">
                        <div className="coach-waiting-icon">
                            <span className="coach-waiting-icon-text">⏳</span>
                        </div>
                    </div>
                    <div className="coach-waiting-message">
                        <h3 className="coach-waiting-title">אנא המתן עד שמנהל הארגון יסדיר את אמצעי התשלום</h3>
                        <p className="coach-waiting-subtitle">
                            תקופת הניסיון של הארגון שלך הסתיימה. אנא המתן שמנהל הארגון יסדיר את אמצעי התשלום בשביל להמשיך להשתמש במערכת.
                        </p>
                    </div>
                </div>
            ) : paymentStatus === "error" ? (
                <div className="error-state-container">
                    <div className="error-icon-wrapper">
                        <div className="error-icon">
                            <span className="error-icon-text">⚠️</span>
                        </div>
                    </div>
                    <div className="error-message">
                        <h3 className="error-title">נראה שלא הצלחנו להשלים את תהליך רישום האשראי</h3>
                        <p className="error-subtitle">שננסה שוב?</p>
                        <div className="error-actions">
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleRetry}
                                className="retry-button"
                            >
                                נסה שוב
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Friendly Header with Icon */}
                    <div className="text-center mb-1">
                        <div className="trial-ended-header mb-2">
                            <div className="trial-icon-wrapper mb-2">
                                <div className="trial-icon">
                                    <span className="trial-icon-text">
                                        {currentScenario === 'A2' ? '⏰' :
                                            currentScenario === 'D1' || currentScenario === 'D2' ? '❌' :
                                                currentScenario === 'B' || currentScenario === 'F' || currentScenario === 'G' ? '⚠️' : '⏰'}
                                    </span>
                                </div>
                            </div>
                            <h2 className="trial-title mb-1">{getModalTitle()}</h2>
                            <p className="trial-subtitle text-muted">
                                {getModalSubtitle()}
                            </p>
                        </div>
                    </div>

                    {/* Package Summary - Enhanced Styling */}
                    <Card className="mb-3 border-0 shadow-sm package-summary-card" style={{
                        background: planName === "bronze"
                            ? 'linear-gradient(135deg, #cd7f32 0%, #b8860b 100%)'
                            : planName === "silver"
                                ? 'linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%)'
                                : 'linear-gradient(135deg, #ffd700 0%, #ffb347 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="text-center p-3">
                            <div className="package-badge mb-2">
                                <span className="package-badge-text">
                                    חבילת {planName === "bronze" ? "ארד" : planName === "silver" ? "כסף" : "זהב"}
                                </span>
                            </div>
                            <h3 className="mb-2" style={{ fontSize: '1.2rem', fontWeight: '600', direction: 'rtl' }}>
                                ₪{planPrices[planName] || "N/A"} לחודש
                            </h3>
                        </Card.Body>
                    </Card>

                    {/* Payment Processing Status */}
                    {isLoadingPayment && (
                        <Card className="mb-3 border-info">
                            <Card.Body className="text-center">
                                <Spinner animation="border" variant="info" className="me-2" />
                                <span>מכין את תהליך התשלום...</span>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Payment Iframe */}
                    {paymentIframe && !isLoadingPayment && !paymentError && (
                        <Card className="mb-3">
                            <Card.Header style={{ direction: "rtl", textAlign: "right" }}>
                                <h6 className="mb-0" style={{ direction: "rtl", textAlign: "right" }}>
                                    <FaCreditCard className="me-2" style={{ marginLeft: "10px" }} />
                                    טופס רישום כרטיס אשראי מאובטח
                                </h6>
                            </Card.Header>
                            {!registrationDocId && (
                                <div className="p-3 bg-warning bg-opacity-10 border-start border-warning">
                                    <small className="text-muted">
                                        <strong>הערה:</strong> יש להשלים את תהליך הרישום בטופס לפני
                                        לחיצה על כפתור "סיום"
                                    </small>
                                </div>
                            )}
                            <Card.Body>

                                {/* Payment Iframe Container */}
                                <div
                                    style={{
                                        width: "100%",
                                        minHeight: "600px",
                                        border: "1px solid #dee2e6",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        position: "relative",
                                    }}
                                >
                                    {iframeLoading && (
                                        <div className="text-center p-4">
                                            <Spinner animation="border" variant="primary" className="me-2" />
                                            <span>טוען טופס תשלום...</span>
                                        </div>
                                    )}
                                    <iframe
                                        key={`payment-iframe-${registrationDocId || 'no-reg-id'}`}
                                        src={paymentIframe}
                                        title="Payment Gateway"
                                        width="100%"
                                        height="600px"
                                        allow="payment"
                                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
                                        style={{
                                            border: "none",
                                            width: "100%",
                                            height: "100%",
                                            minHeight: "600px",
                                            display: iframeLoading ? "none" : "block",
                                        }}
                                        onLoad={handleIframeLoad}
                                        onError={handleIframeError}
                                    />
                                </div>

                                {/* Security Notice */}
                                <div className="mt-3 p-3 bg-light rounded" style={{ direction: "rtl", textAlign: "right" }}>
                                    <div className="d-flex align-items-start mb-3" style={{ flexDirection: "row-reverse" }}>
                                        <FaLock className="text-success ms-2 mt-1" />
                                        <div>
                                            <h6 className="mb-1">רישום מאובטח</h6>
                                            <p className="text-muted small mb-0">
                                                פרטי כרטיס האשראי שלך מוצפנים ומאובטחים. אנו משתמשים בשער התשלומים
                                                המאובטח של Pelecard עם הצפנת SSL בתקן תעשייתי. פרטי הכרטיס
                                                שלך לא נשמרים במערכת שלנו.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Payment Status Display */}
                    {paymentStatus === "success" && (
                        <div className="text-center">
                            <div className="success-celebration">
                                <div className="success-icon mb-3">
                                    <span className="success-icon-text">🎉</span>
                                </div>
                                <Alert variant="success" className="success-alert">
                                    <h4 className="mb-3">✅ התשלום הושלם בהצלחה!</h4>
                                    <p className="mb-0">כרטיס האשראי שלך נרשם בהצלחה. אתה יכול כעת להשתמש במערכת.</p>
                                </Alert>
                            </div>
                        </div>
                    )}

                    {isCheckingPayment && (
                        <div className="text-center">
                            <Spinner animation="border" variant="info" className="me-2" />
                            <span>בודק את סטטוס התשלום...</span>
                        </div>
                    )}
                </>
            )}
        </CustomModal>
    );
};

export default InAppPaymentModal;
